import { z } from 'zod'

import { FinancialCommandResultSchema } from './financial-command'
import { UnbilledTimeSummarySchema } from './invoice'
import { ModuleTypeEnum } from './module-type'
import { ContractorInvoiceEligibilityPolicyEnum } from './organization-settings'
import { decimalSchema } from './prisma-zod'

export enum ContractorTypeEnum {
  INDIVIDUAL = 'INDIVIDUAL',
  SOLE_PROP = 'SOLE_PROP',
  LLC = 'LLC',
  CORPORATION = 'CORPORATION',
}

export enum ContractorInvoiceStatusEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PARTIALLY_TIME_APPROVED = 'PARTIALLY_TIME_APPROVED',
  TIME_APPROVED = 'TIME_APPROVED',
  PAYROLL_APPROVED = 'PAYROLL_APPROVED',
  PAID = 'PAID',
  ON_HOLD = 'ON_HOLD',
  RECALLED = 'RECALLED',
  VOID = 'VOID',
}

export enum ContractorInvoiceBillingCadenceEnum {
  PAY_PERIOD = 'PAY_PERIOD',
  CUSTOM_RANGE = 'CUSTOM_RANGE',
}

export enum ContractorInvoiceDeliveryMethodEnum {
  EMAIL = 'EMAIL',
  DOWNLOAD = 'DOWNLOAD',
  MANUAL = 'MANUAL',
}

export const ContractorAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().optional().nullable(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string().default('US'),
})

export type ContractorAddress = z.infer<typeof ContractorAddressSchema>

export const ContractorProfileSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  contractorType: z.nativeEnum(ContractorTypeEnum),
  firstName: z.string(),
  lastName: z.string(),
  billingEmail: z.string().email(),
  billingAddress: ContractorAddressSchema,
  taxIdType: z.string().optional().nullable(),
  taxIdMasked: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  remitToName: z.string().optional().nullable(),
  paymentInstructions: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ContractorProfile = z.infer<typeof ContractorProfileSchema>

export const UpsertContractorProfileInputSchema = ContractorProfileSchema.omit({
  id: true,
  organizationId: true,
  memberId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  taxId: z.string().optional(),
})

export type UpsertContractorProfileInput = z.infer<
  typeof UpsertContractorProfileInputSchema
>

export const ContractorInvoiceTimeEntryReferenceSchema = z.object({
  timeEntryId: z.string().uuid(),
})

export type ContractorInvoiceTimeEntryReference = z.infer<
  typeof ContractorInvoiceTimeEntryReferenceSchema
>

export const ContractorInvoiceLineSchema = z.object({
  id: z.string().uuid(),
  contractorInvoiceId: z.string().uuid(),
  description: z.string(),
  groupKey: z.string(),
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  moduleName: z.string().nullable().optional(),
  hours: decimalSchema,
  rate: decimalSchema,
  amount: decimalSchema,
  sortOrder: z.number().int(),
  timeEntries: z.array(ContractorInvoiceTimeEntryReferenceSchema).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ContractorInvoiceLine = z.infer<typeof ContractorInvoiceLineSchema>

export const ContractorInvoicePayPeriodSchema = z.object({
  id: z.string().uuid(),
  contractorInvoiceId: z.string().uuid(),
  payPeriodId: z.string().uuid(),
  userPayPeriodId: z.string().uuid(),
  memberId: z.string().uuid(),
  includedAt: z.date(),
  payPeriod: z.any().optional(),
  userPayPeriod: z.any().optional(),
})

export type ContractorInvoicePayPeriod = z.infer<
  typeof ContractorInvoicePayPeriodSchema
>

export const ContractorInvoiceDeliveryRecipientSchema = z.object({
  memberId: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
})

export const ContractorInvoiceExpenseReimbursementItemSchema = z.object({
  expenseId: z.string().uuid(),
  merchant: z.string(),
  amount: z.number(),
  expenseDate: z.string().optional(),
})

export const ContractorInvoiceExpenseReimbursementsSchema = z.object({
  count: z.number().int(),
  totalAmount: z.number(),
  items: z.array(ContractorInvoiceExpenseReimbursementItemSchema).default([]),
})

export type ContractorInvoiceExpenseReimbursements = z.infer<
  typeof ContractorInvoiceExpenseReimbursementsSchema
>

// Hours on an invoice split into billable (billed to a client) and
// non-billable (internal) time. Whether non-billable time is paid follows the
// invoice's payBasis: ALL_HOURS pays it, BILLABLE_HOURS lists it as unpaid,
// RETAINER covers all of it.
export const ContractorInvoiceHoursSummarySchema = z.object({
  billableHours: z.number(),
  nonBillableHours: z.number(),
  totalHours: z.number(),
})

export type ContractorInvoiceHoursSummary = z.infer<
  typeof ContractorInvoiceHoursSummarySchema
>

// Time per client. `entityId: null` is the organization's own (internal) work.
export const ContractorInvoiceClientSummarySchema = z.object({
  entityId: z.string().uuid().nullable(),
  name: z.string(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  amount: z.number(),
})

export type ContractorInvoiceClientSummary = z.infer<
  typeof ContractorInvoiceClientSummarySchema
>

// Time per client and project. `projectId: null` is time logged on the
// client itself (or internal work) rather than one of its projects.
export const ContractorInvoiceProjectSummarySchema = z.object({
  entityId: z.string().uuid().nullable(),
  clientName: z.string(),
  projectId: z.string().uuid().nullable(),
  projectName: z.string().nullable(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  amount: z.number(),
})

export type ContractorInvoiceProjectSummary = z.infer<
  typeof ContractorInvoiceProjectSummarySchema
>

export const ContractorInvoiceSnapshotSchema = z
  .object({
    contractorProfile: z.any(),
    organization: z.any(),
    payPeriods: z.array(z.any()).default([]),
    lines: z.array(z.any()).default([]),
    unbilledTime: UnbilledTimeSummarySchema.optional(),
    // The contractor's pay basis when the invoice was built. Older invoices lack
    // it and were built as BILLABLE_HOURS.
    payBasis: z.enum(['ALL_HOURS', 'BILLABLE_HOURS', 'RETAINER']).optional(),
    hours: ContractorInvoiceHoursSummarySchema.optional(),
    byClient: z.array(ContractorInvoiceClientSummarySchema).optional(),
    byProject: z.array(ContractorInvoiceProjectSummarySchema).optional(),
    expenseReimbursements:
      ContractorInvoiceExpenseReimbursementsSchema.optional(),
    delivery: z
      .object({
        method: z.nativeEnum(ContractorInvoiceDeliveryMethodEnum),
        sentAt: z.string(),
        recipients: z.array(ContractorInvoiceDeliveryRecipientSchema),
      })
      .optional(),
  })
  .passthrough()

export type ContractorInvoiceSnapshot = z.infer<
  typeof ContractorInvoiceSnapshotSchema
>

/** Public relation projection; never exposes member settings or private identity snapshots. */
export const ContractorInvoiceMemberSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid().nullable(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  status: z.string().optional(),
  role: z.string().optional(),
  user: z
    .object({
      id: z.string().uuid(),
      displayName: z.string().nullable().optional(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      email: z.string().nullable().optional(),
      image: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  contractorProfile: ContractorProfileSchema.nullable().optional(),
})

export const ContractorInvoiceSchema = z.object({
  financialCommand: FinancialCommandResultSchema.optional(),
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  invoiceNumber: z.string(),
  status: z.nativeEnum(ContractorInvoiceStatusEnum),
  currency: z.string(),
  invoiceStartDate: z.date(),
  invoiceEndDate: z.date(),
  billingCadence: z.nativeEnum(ContractorInvoiceBillingCadenceEnum),
  totalHours: decimalSchema,
  subtotal: decimalSchema,
  totalAmount: decimalSchema,
  notes: z.string().nullable().optional(),
  submittedAt: z.date().nullable().optional(),
  timeApprovedAt: z.date().nullable().optional(),
  payrollApprovedAt: z.date().nullable().optional(),
  paidAt: z.date().nullable().optional(),
  paymentDueDate: z.date().nullable().optional(),
  holdReason: z.string().nullable().optional(),
  heldAt: z.date().nullable().optional(),
  preHoldStatus: z
    .nativeEnum(ContractorInvoiceStatusEnum)
    .nullable()
    .optional(),
  deliveredAt: z.date().nullable().optional(),
  deliveredMethod: z
    .nativeEnum(ContractorInvoiceDeliveryMethodEnum)
    .nullable()
    .optional(),
  snapshot: ContractorInvoiceSnapshotSchema.default({
    contractorProfile: {},
    organization: {},
    payPeriods: [],
    lines: [],
  }),
  createdAt: z.date(),
  updatedAt: z.date(),
  payPeriods: z.array(ContractorInvoicePayPeriodSchema).optional(),
  lines: z.array(ContractorInvoiceLineSchema).optional(),
  member: ContractorInvoiceMemberSchema.optional(),
})

export type ContractorInvoice = z.infer<typeof ContractorInvoiceSchema>

export const PreviewContractorInvoiceInputSchema = z.object({
  userPayPeriodIds: z.array(z.string().uuid()).min(1),
  notes: z.string().optional(),
  billingCadence: z
    .nativeEnum(ContractorInvoiceBillingCadenceEnum)
    .default(ContractorInvoiceBillingCadenceEnum.PAY_PERIOD),
  // Non-billable time is always listed on the invoice now; the flag
  // stays for older clients and defaults on.
  includeUnbilledTime: z.boolean().default(true),
  includeExpenses: z.boolean().optional(),
})

export type PreviewContractorInvoiceInput = z.infer<
  typeof PreviewContractorInvoiceInputSchema
>

export const CreateContractorInvoiceInputSchema =
  PreviewContractorInvoiceInputSchema

export const MarkContractorInvoicePaidInputSchema = z.object({
  expectedUserId: z.string().uuid().optional(),
  operationId: z.string().uuid().optional(),
  paidAt: z.string().datetime().optional(),
})
export type MarkContractorInvoicePaidInput = z.infer<
  typeof MarkContractorInvoicePaidInputSchema
>

export const HoldContractorInvoiceInputSchema = z.object({
  holdReason: z.string().min(1).max(500),
})
export type HoldContractorInvoiceInput = z.infer<
  typeof HoldContractorInvoiceInputSchema
>

export const SetContractorInvoicePaymentDueDateInputSchema = z.object({
  paymentDueDate: z.string().nullable(),
})
export type SetContractorInvoicePaymentDueDateInput = z.infer<
  typeof SetContractorInvoicePaymentDueDateInputSchema
>

export type CreateContractorInvoiceInput = z.infer<
  typeof CreateContractorInvoiceInputSchema
>

export const ContractorInvoiceEligiblePayPeriodSchema = z.object({
  payPeriodId: z.string().uuid(),
  userPayPeriodId: z.string().uuid(),
  memberId: z.string().uuid(),
  name: z.string(),
  periodType: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  userPayPeriodStatus: z.string(),
  invoiceEligibilityPolicy: z.nativeEnum(
    ContractorInvoiceEligibilityPolicyEnum,
  ),
  totalHours: decimalSchema,
  approvedHours: decimalSchema,
  // Approved hours split into billable and non-billable time.
  approvedBillableHours: decimalSchema.optional(),
  // The member's pay basis; approvedBillableHours / approvedNonBillableHours
  // tell how those hours are paid under it.
  payBasis: z.enum(['ALL_HOURS', 'BILLABLE_HOURS', 'RETAINER']).optional(),
  retainerAmount: z.number().nullable().optional(),
  approvedNonBillableHours: decimalSchema.optional(),
  pendingHours: decimalSchema,
  canInvoice: z.boolean(),
  blockingReason: z.string().optional().nullable(),
})

export type ContractorInvoiceEligiblePayPeriod = z.infer<
  typeof ContractorInvoiceEligiblePayPeriodSchema
>
